import { useState, useEffect, Fragment } from "react";
import { useGetLogs, type ActivityLog, type LogFilters } from "../../../api/routes/logs";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../components/ui/table";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Search } from "lucide-react";
import moment from "moment";

const CATEGORIES = [
    { value: "all", label: "All" },
    { value: "audit", label: "Audit" },
    { value: "mqtt", label: "MQTT" },
    { value: "device", label: "Device" },
    { value: "auth", label: "Auth" },
];

const RESOURCE_TYPES = [
    { value: "all", label: "All Resources" },
    { value: "device", label: "Device" },
    { value: "business", label: "Business" },
    { value: "user", label: "User" },
    { value: "model", label: "Model" },
    { value: "model_attribute", label: "Model Attribute" },
    { value: "can_field", label: "CAN Field" },
];

const categoryColors: Record<string, string> = {
    audit: "bg-blue-100 text-blue-800",
    mqtt: "bg-green-100 text-green-800",
    device: "bg-purple-100 text-purple-800",
    auth: "bg-amber-100 text-amber-800",
};

const DiffView = ({ beforeData, afterData }: { beforeData: Record<string, any> | null; afterData: Record<string, any> | null }) => {
    if (!beforeData && !afterData) return null;

    return (
        <div className="grid grid-cols-2 gap-4 text-xs">
            {beforeData && (
                <div>
                    <p className="font-medium text-muted-foreground mb-1">Before</p>
                    <div className="bg-red-50 rounded p-2 space-y-0.5">
                        {Object.entries(beforeData).map(([key, value]) => (
                            <div key={key}>
                                <span className="text-muted-foreground">{key}:</span>{" "}
                                <span className="font-mono">{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {afterData && (
                <div>
                    <p className="font-medium text-muted-foreground mb-1">After</p>
                    <div className="bg-green-50 rounded p-2 space-y-0.5">
                        {Object.entries(afterData).map(([key, value]) => (
                            <div key={key}>
                                <span className="text-muted-foreground">{key}:</span>{" "}
                                <span className="font-mono">{typeof value === "object" ? JSON.stringify(value) : String(value)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const LogsRoute = () => {
    const [filters, setFilters] = useState<LogFilters>({
        page: 1,
        pageSize: 50,
    });
    const [searchInput, setSearchInput] = useState("");
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const { data, isLoading } = useGetLogs(filters);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters((prev) => ({
                ...prev,
                search: searchInput || undefined,
                page: 1,
            }));
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const toggleRow = (id: string) => {
        setExpandedRows((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const updateFilter = (key: keyof LogFilters, value: string) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value === "all" ? undefined : value,
            page: 1,
        }));
    };

    const pagination = data?.pagination;

    return (
        <div className="mx-auto py-10 md:px-32 px-4">
            <h1 className="text-2xl font-bold mb-6">Activity Logs</h1>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
                {/* Category chips */}
                <div className="flex gap-1">
                    {CATEGORIES.map((cat) => (
                        <Button
                            key={cat.value}
                            variant={(filters.category || "all") === cat.value ? "default" : "outline"}
                            size="sm"
                            onClick={() => updateFilter("category", cat.value)}
                        >
                            {cat.label}
                        </Button>
                    ))}
                </div>

                {/* Resource type */}
                <Select
                    value={filters.resourceType || "all"}
                    onValueChange={(v) => updateFilter("resourceType", v)}
                >
                    <SelectTrigger className="w-[180px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {RESOURCE_TYPES.map((rt) => (
                            <SelectItem key={rt.value} value={rt.value}>
                                {rt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                {/* Search */}
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search logs..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        className="pl-9"
                    />
                </div>

                {/* Date from */}
                <Input
                    type="date"
                    className="w-[160px]"
                    value={filters.from || ""}
                    onChange={(e) => updateFilter("from", e.target.value || "all")}
                />

                {/* Date to */}
                <Input
                    type="date"
                    className="w-[160px]"
                    value={filters.to || ""}
                    onChange={(e) => updateFilter("to", e.target.value || "all")}
                />
            </div>

            {/* Table */}
            <div className="rounded-md border bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[30px]"></TableHead>
                            <TableHead className="w-[170px]">Timestamp</TableHead>
                            <TableHead className="w-[80px]">Category</TableHead>
                            <TableHead className="w-[120px]">Action</TableHead>
                            <TableHead>Resource</TableHead>
                            <TableHead>Actor</TableHead>
                            <TableHead>Message</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                    Loading...
                                </TableCell>
                            </TableRow>
                        ) : !data?.logs?.length ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                    No logs found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            data.logs.map((log: ActivityLog) => {
                                const hasDetails = log.beforeData || log.afterData || log.metadata;
                                const isExpanded = expandedRows.has(log.id);

                                return (
                                    <Fragment key={log.id}>
                                        <TableRow
                                            className={hasDetails ? "cursor-pointer hover:bg-muted/50" : ""}
                                            onClick={() => hasDetails && toggleRow(log.id)}
                                        >
                                            <TableCell className="px-2">
                                                {hasDetails && (
                                                    isExpanded
                                                        ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                                        : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                )}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs whitespace-nowrap">
                                                {moment(log.createdAt).format("YYYY-MM-DD h:mm:ss A")}
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={categoryColors[log.category] || ""} variant="outline">
                                                    {log.category}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm">{log.action}</TableCell>
                                            <TableCell className="text-sm">
                                                {log.resourceName || log.resourceType || "-"}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {log.actorName || "-"}
                                            </TableCell>
                                            <TableCell className="text-sm max-w-[300px] truncate">
                                                {log.message}
                                            </TableCell>
                                        </TableRow>
                                        {isExpanded && (
                                            <TableRow>
                                                <TableCell colSpan={7} className="bg-muted/20 px-8 py-4">
                                                    <DiffView beforeData={log.beforeData} afterData={log.afterData} />
                                                    {log.metadata && (
                                                        <div className="mt-3 text-xs">
                                                            <p className="font-medium text-muted-foreground mb-1">Metadata</p>
                                                            <pre className="bg-muted rounded p-2 overflow-x-auto">
                                                                {JSON.stringify(log.metadata, null, 2)}
                                                            </pre>
                                                        </div>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                        Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total}
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={pagination.page <= 1}
                            onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) - 1 }))}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-sm">
                            Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={pagination.page >= pagination.totalPages}
                            onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page || 1) + 1 }))}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LogsRoute;
